// src/pages/finance-portal/inventory/InventoryDashboard.js
//
// Inventory Dashboard
//
// KPI cards: total SKUs, inventory value, low stock count, incoming PO
// value, movements last 7 days. Three panels below:
//   1. Low-stock alerts (needs_reorder = true)
//   2. Reorder candidates (with auto-reorder button)
//   3. Top SKUs by value

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
    Grid, IconButton, LinearProgress, Paper, Skeleton, Stack,
    Table, TableBody, TableCell, TableHead, TableRow,
    Tooltip, Typography,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK,
    formatCurrency, formatQty, PRODUCT_STATUS_COLORS, statusChip,
} from './components/inventoryFormatters';

function StatCard({ icon, label, value, subtitle, color = INV_ACCENT, onClick }) {
    return (
        <Card
            onClick={onClick}
            sx={{
                borderRadius: '16px',
                border: '1px solid #e2e8f0',
                boxShadow: 'none',
                cursor: onClick ? 'pointer' : 'default',
                transition: 'all 0.2s ease',
                '&:hover': onClick
                    ? { borderColor: color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.06)' }
                    : {},
            }}
        >
            <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box
                        sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            bgcolor: `${color}14`, display: 'flex',
                            alignItems: 'center', justifyContent: 'center', color,
                        }}
                    >
                        {icon}
                    </Box>
                    {onClick && <ArrowForwardIcon sx={{ color: '#94a3b8' }} />}
                </Box>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', color: '#64748b', fontWeight: 500 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.6rem', fontWeight: 700, color: '#0f172a', mt: 0.5 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.78rem', color: '#64748b', mt: 0.5 }}>
                        {subtitle}
                    </Typography>
                )}
            </CardContent>
        </Card>
    );
}

export default function InventoryDashboard() {
    const navigate = useNavigate();
    const [kpis, setKpis] = useState(null);
    const [valuation, setValuation] = useState(null);
    const [reorderCandidates, setReorderCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [runningReorder, setRunningReorder] = useState(false);
    const [error, setError] = useState(null);
    const [reorderMessage, setReorderMessage] = useState(null);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [kRes, vRes, rRes] = await Promise.all([
                axiosInstance.get('/api/finance/inventory/dashboard/kpis'),
                axiosInstance.get('/api/finance/inventory/valuation'),
                axiosInstance.get('/api/finance/inventory/reorder/scan'),
            ]);
            if (kRes.data?.success) setKpis(kRes.data.data);
            if (vRes.data?.success) setValuation(vRes.data.data);
            if (rRes.data?.success) setReorderCandidates(rRes.data.data || []);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load dashboard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleGenerateReorder = async () => {
        if (!window.confirm(`Generate draft POs for ${reorderCandidates.length} SKU(s)?`)) return;
        setRunningReorder(true);
        setReorderMessage(null);
        try {
            const res = await axiosInstance.post('/api/finance/inventory/reorder/generate', {});
            if (res.data?.success) {
                setReorderMessage(`${res.data.count} draft PO(s) created`);
                fetchAll();
            } else {
                setReorderMessage(res.data?.message || 'Failed');
            }
        } catch (e) {
            setReorderMessage(e.response?.data?.message || 'Error generating POs');
        } finally {
            setRunningReorder(false);
        }
    };

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.65rem', fontWeight: 700, color: '#0f172a' }}>
                        Inventory Dashboard
                    </Typography>
                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.9rem', color: '#64748b', mt: 0.5 }}>
                        Stock, purchase orders, and COGS insight
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchAll}
                        sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', borderColor: '#cbd5e1', color: '#334155' }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AutoAwesomeIcon />}
                        disabled={reorderCandidates.length === 0 || runningReorder}
                        onClick={handleGenerateReorder}
                        sx={{
                            fontFamily: FONT_FAMILY, textTransform: 'none',
                            bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK },
                        }}
                    >
                        {runningReorder ? 'Working…' : `Auto-Reorder (${reorderCandidates.length})`}
                    </Button>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {reorderMessage && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setReorderMessage(null)}>{reorderMessage}</Alert>}

            {/* KPI Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    {loading ? <Skeleton variant="rectangular" height={130} sx={{ borderRadius: '16px' }} /> : (
                        <StatCard
                            icon={<Inventory2Icon />} label="Total SKUs"
                            value={formatQty(kpis?.total_skus || 0, 0)}
                            onClick={() => navigate('/finance-portal/inventory/products')}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    {loading ? <Skeleton variant="rectangular" height={130} sx={{ borderRadius: '16px' }} /> : (
                        <StatCard
                            icon={<AttachMoneyIcon />} label="Inventory Value"
                            value={formatCurrency(kpis?.total_inventory_value || 0)}
                            subtitle="On-hand, all locations"
                            color="#059669"
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    {loading ? <Skeleton variant="rectangular" height={130} sx={{ borderRadius: '16px' }} /> : (
                        <StatCard
                            icon={<WarningAmberIcon />} label="Low Stock"
                            value={formatQty(kpis?.low_stock_count || 0, 0)}
                            subtitle="Below reorder point"
                            color="#dc2626"
                            onClick={() => navigate('/finance-portal/inventory/reorder')}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    {loading ? <Skeleton variant="rectangular" height={130} sx={{ borderRadius: '16px' }} /> : (
                        <StatCard
                            icon={<MoveToInboxIcon />} label="Incoming PO Value"
                            value={formatCurrency(kpis?.incoming_po_value || 0)}
                            subtitle="Approved & not received"
                            color="#7c3aed"
                            onClick={() => navigate('/finance-portal/inventory/purchase-orders')}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    {loading ? <Skeleton variant="rectangular" height={130} sx={{ borderRadius: '16px' }} /> : (
                        <StatCard
                            icon={<TrendingUpIcon />} label="Movements (7d)"
                            value={formatQty(kpis?.movements_last_7_days || 0, 0)}
                            subtitle="Receipts, sales, adjustments"
                            color="#0891b2"
                        />
                    )}
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Reorder candidates */}
                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' }}>
                                Reorder Candidates
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => navigate('/finance-portal/inventory/reorder')}
                                sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', color: INV_ACCENT }}
                            >
                                View all
                            </Button>
                        </Box>
                        {loading ? (
                            <LinearProgress sx={{ my: 3 }} />
                        ) : reorderCandidates.length === 0 ? (
                            <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', textAlign: 'center', py: 4 }}>
                                No SKUs below reorder point
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Location</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">On Hand</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Reorder Pt</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Recommended</TableCell>
                                        <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Vendor</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reorderCandidates.slice(0, 8).map((c) => (
                                        <TableRow key={`${c.product_id}-${c.location_id}`}
                                                  hover
                                                  onClick={() => navigate(`/finance-portal/inventory/products/${c.product_id}`)}
                                                  sx={{ cursor: 'pointer' }}>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY }}>
                                                <Box>
                                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }}>
                                                        {c.sku}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.75rem', color: '#64748b' }}>
                                                        {c.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }}>{c.location_code}</TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }} align="right">
                                                {formatQty(c.on_hand)}
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }} align="right">
                                                {formatQty(c.reorder_point)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip size="small" label={formatQty(c.recommended_qty)}
                                                      style={statusChip('ACTIVE', PRODUCT_STATUS_COLORS)}
                                                      sx={{ fontWeight: 600, fontFamily: FONT_FAMILY }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem' }}>
                                                {c.preferred_vendor_name || <Chip size="small" label="No vendor" color="warning" />}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>

                {/* Top SKUs by value */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 2, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.05rem', fontWeight: 600, color: '#0f172a', mb: 1 }}>
                            Top SKUs by Inventory Value
                        </Typography>
                        {loading ? <LinearProgress sx={{ my: 3 }} /> : (valuation?.by_product?.length || 0) === 0 ? (
                            <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', textAlign: 'center', py: 4 }}>
                                No valued stock
                            </Typography>
                        ) : (
                            <Table size="small">
                                <TableBody>
                                    {(valuation.by_product || []).slice(0, 8).map((p) => {
                                        const pct = valuation.total_value > 0
                                            ? (p.value / valuation.total_value) * 100 : 0;
                                        return (
                                            <TableRow key={p.product_id} hover
                                                      onClick={() => navigate(`/finance-portal/inventory/products/${p.product_id}`)}
                                                      sx={{ cursor: 'pointer' }}>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, width: '45%' }}>
                                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }}>
                                                        {p.sku}
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.72rem', color: '#64748b' }}>
                                                        {p.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ width: '30%' }}>
                                                    <LinearProgress
                                                        variant="determinate" value={Math.min(pct, 100)}
                                                        sx={{
                                                            height: 6, borderRadius: 3, bgcolor: '#e2e8f0',
                                                            '& .MuiLinearProgress-bar': { bgcolor: INV_ACCENT },
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontSize: '0.82rem', fontWeight: 600 }} align="right">
                                                    {formatCurrency(p.value)}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
